import type { ApplicationStatus } from '@/components/ui';

/**
 * Application status handling, shared by the list (/profile/applications) and
 * the read-only view (/profile/applications/[id]/view).
 *
 * Both surfaces show an Edit affordance under the same rules, and both have to
 * map the API's free-text status onto our enum. Keeping either copy local
 * invites the two pages to disagree — e.g. a status added to one list and not
 * the other, which would silently show Edit in one place and not the other.
 */

/** The API sends free-text status values; match loosely and map onto the enum. */
export function normalizeApplicationStatus(raw?: string | null): ApplicationStatus {
  const s = (raw ?? '').toLowerCase();
  // The API says "accepted" for an approved application, which shares no
  // substring with "approve" — so it used to fall through to `unknown` and
  // render as the neutral "Draft" tag.
  if (s.includes('approve') || s.includes('accept')) return 'approved';
  if (s.includes('reject')) return 'rejected';
  if (s.includes('submit')) return 'submitted';
  if (s.includes('draft')) return 'draft';
  if (s.includes('in_progress') || s.includes('in progress')) return 'in_progress';
  return 'unknown';
}

/**
 * Statuses whose application can still be edited.
 *
 * Approved and rejected are terminal — the design shows no Edit link or button
 * for either. `unknown` is excluded deliberately: if we can't read the status,
 * don't offer a destructive action.
 */
const EDITABLE_STATUSES: ReadonlySet<ApplicationStatus> = new Set([
  'draft',
  'in_progress',
  'submitted',
]);

export function canEditApplication(status: ApplicationStatus): boolean {
  return EDITABLE_STATUSES.has(status);
}

/**
 * True when opening the editor first has to revert the application to a draft.
 *
 * Only submitted applications need that, and it's destructive — the applicant
 * drops out of consideration until they resubmit — so it's gated behind
 * <EditApplicationModal />. Drafts open straight away.
 */
export function needsEditConfirmation(status: ApplicationStatus): boolean {
  return status === 'submitted';
}
